import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Input, SimpleGrid } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Context } from "@/App";
import { SPACING } from "@/generic/config";
import { DialogBackdrop, DialogBody, DialogCloseTrigger, DialogContent, DialogHeader, DialogRoot } from "@/components/ui/dialog";
import Text from "./Text";
import { PRODUCT_ICON_CATEGORIES, ProductIconCategory, productIcon, searchProductIcons } from "./product-icons";

/**
 * Pick a product icon from a searchable grid (search works in English and
 * Swedish), filtered by category. Modelled on the just-draw icon picker, in
 * the OMS's own look.
 */
const ProductIconPicker = ({ value, onChange }: { value: string | null | undefined, onChange: (name: string | null) => void }) => {
    const { translations } = React.useContext(Context);
    const t = translations.model.product.iconPicker;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState<ProductIconCategory | null>(null);
    const current = productIcon(value);
    const results = useMemo(() => searchProductIcons(query, category), [query, category]);

    const pick = (name: string | null) => {
        onChange(name);
        setOpen(false);
    };

    return (
        <>
            <Flex gap={SPACING * 2} alignItems="center">
                <Flex w={14} h={14} borderWidth="1px" borderRadius="md" alignItems="center" justifyContent="center" fontSize="2xl" data-testid="product-icon-current">
                    {current ? <FontAwesomeIcon icon={current} /> : <Text variant="small" color="fg.muted">{t.none}</Text>}
                </Flex>
                <Button variant="outline" onClick={() => setOpen(true)} data-testid="product-icon-change">{t.change}</Button>
                {current && <Button variant="ghost" onClick={() => onChange(null)}>{t.remove}</Button>}
            </Flex>

            <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} size="lg" placement="center" scrollBehavior="inside">
                <DialogBackdrop />
                <DialogContent>
                    <DialogHeader><Text variant="title">{t.title}</Text></DialogHeader>
                    <DialogCloseTrigger />
                    <DialogBody pb={SPACING * 3}>
                        <Input autoFocus placeholder={t.search} value={query} onChange={(e) => setQuery(e.target.value)} mb={SPACING * 2} data-testid="product-icon-search" />
                        <Flex gap={SPACING} wrap="wrap" mb={SPACING * 2}>
                            <Button size="xs" variant={category === null ? "solid" : "outline"} onClick={() => setCategory(null)}>{t.all}</Button>
                            {PRODUCT_ICON_CATEGORIES.map(c => (
                                <Button key={c} size="xs" variant={category === c ? "solid" : "outline"} onClick={() => setCategory(c)}>{t.categories[c]}</Button>
                            ))}
                        </Flex>
                        <SimpleGrid minChildWidth="64px" gap={SPACING}>
                            {results.map(({ icon }) => {
                                const selected = icon.iconName === value;
                                return (
                                    <Box
                                        as="button"
                                        key={icon.iconName}
                                        title={icon.iconName}
                                        aria-label={icon.iconName}
                                        aria-pressed={selected}
                                        onClick={() => pick(icon.iconName)}
                                        h="64px"
                                        fontSize="2xl"
                                        borderWidth={selected ? "2px" : "1px"}
                                        borderColor={selected ? "fg" : "border"}
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                        _hover={{ bg: "bg.muted" }}
                                        data-testid={`product-icon-${icon.iconName}`}
                                    >
                                        <FontAwesomeIcon icon={icon} />
                                    </Box>
                                );
                            })}
                        </SimpleGrid>
                        {results.length === 0 && <Text mt={SPACING * 2} color="fg.muted">{t.noResults}</Text>}
                    </DialogBody>
                </DialogContent>
            </DialogRoot>
        </>
    );
};

export default ProductIconPicker;
